import { useEffect, useState } from "react";
import api from "../utils/api";
import {
  LineChart,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Line,
} from "recharts";

export default function Dashboard() {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalProducts: [],
    totalContracts: 0,
    totalContentOwners: 0,
    contractsByMonth: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Fetch all data at once
        const [usersRes, productsRes, contractsRes, contentOwnersRes] =
          await Promise.all([
            api.get("/users", { withCredentials: true }),
            api.get("/products", { withCredentials: true }),
            api.get("/contracts?limit=10000", { withCredentials: true }),
            api.get("/contentOwners", { withCredentials: true }),
          ]);

        const users = usersRes.data.users || usersRes.data || [];
        const products = productsRes.data.products || productsRes.data || [];
        const contracts =
          contractsRes.data.contracts || contractsRes.data || [];
        const contentOwners =
          contentOwnersRes.data.contentOwners || contentOwnersRes.data || [];

        // Count contracts per month for chart
        const contractsByMonthMap = {};
        contracts
          .filter((c) => !c.isDeleted)
          .forEach((contract) => {
            if (contract.signingDate) {
              const month = new Date(contract.signingDate).toLocaleString(
                "default",
                { month: "short", year: "numeric" },
              );
              contractsByMonthMap[month] =
                (contractsByMonthMap[month] || 0) + 1;
            }
          });
        const contractsByMonth = Object.entries(contractsByMonthMap)
          .sort((a, b) => new Date(a[0]) - new Date(b[0]))
          .map(([month, contracts]) => ({ month, contracts }));

        const contractsByProduct = {};
        contracts
          .filter((c) => !c.isDeleted)
          .forEach((contract) => {
            const productId = contract.productId?._id || contract.productId;
            if (productId) {
              const idStr = productId.toString();
              contractsByProduct[idStr] = (contractsByProduct[idStr] || 0) + 1;
            }
          });

        const productsWithCounts = products
          .filter((p) => !p.isDeleted)
          .map((p) => ({
            ...p,
            contractCount: contractsByProduct[p._id?.toString() || p._id] || 0,
          }));

        setStats({
          totalUsers: users.filter((u) => !u.isDeleted).length,
          totalProducts: productsWithCounts,
          totalContracts: contracts.filter((c) => !c.isDeleted).length,
          totalContentOwners: contentOwners.filter((c) => !c.isDeleted).length,
          contractsByMonth,
        });

        setLoading(false);
      } catch (err) {
        console.error("Error fetching stats:", err);
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  return (
    <>
      {loading ? (
        <div className="p-6 mt-10">
          <h1 className="text-2xl font-bold">Loading Dashboard...</h1>
        </div>
      ) : (
        <div className="p-6 mt-10 max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold mb-8">Dashboard</h1>

          <div className="grid grid-cols-4 gap-8">
            <div className="bg-blue-500 text-white rounded-xl p-8 shadow-lg">
              <h2 className="text-xl font-semibold mb-4">Total Users</h2>
              <p className="text-5xl font-bold">{stats.totalUsers}</p>
            </div>

            <div className="bg-yellow-500 text-white rounded-xl p-8 shadow-lg">
              <h2 className="text-xl font-semibold mb-4">
                Total Content Owners
              </h2>
              <p className="text-5xl font-bold">{stats.totalContentOwners}</p>
            </div>

            <div className="bg-green-500 text-white rounded-xl p-8 shadow-lg">
              <h2 className="text-xl font-semibold mb-4">Total Contracts</h2>
              <p className="text-5xl font-bold">{stats.totalContracts}</p>
            </div>

            <div className="bg-red-500 text-white rounded-xl p-8 shadow-lg">
              <h2 className="text-xl font-semibold mb-4">Total Products</h2>
              <p className="text-5xl font-bold">{stats.totalProducts.length}</p>
            </div>
          </div>

          <div className="mt-8">
            <h2 className="text-2xl font-bold mb-6">Products Overview</h2>
            {stats.totalProducts.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
                {stats.totalProducts.map((product) => (
                  <div
                    key={product._id}
                    className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition flex gap-4"
                  >
                    {product.imageUrl ? (
                      <img
                        src={`${process.env.REACT_APP_API_URL}/${product.imageUrl}`}
                        alt={product.name}
                        className="w-20 h-20 object-cover rounded-md mb-4"
                      />
                    ) : (
                      <div className="w-full h-32 bg-gray-200 rounded-md mb-4 flex items-center justify-center">
                        <span className="text-gray-400">No Image</span>
                      </div>
                    )}
                    <div className="flex flex-col">
                      <h3 className="font-semibold text-gray-800 truncate">
                        {product.name}
                      </h3>
                      <p className="text-3xl font-bold text-blue-600">
                        {product.contractCount || 0}
                      </p>
                      <p className="text-sm text-gray-500">
                        {product.contractCount === 1 ? "Contract" : "Contracts"}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-gray-100 rounded-lg p-8 text-center">
                <p className="text-gray-600">No products available</p>
              </div>
            )}
          </div>

          {stats.contractsByMonth.length > 0 && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold mb-6">Contracts by Month</h2>
              <div className="bg-white rounded-xl shadow-sm p-4 md:p-6">
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart
                    data={stats.contractsByMonth}
                    margin={{ top: 20, right: 20, left: 20, bottom: 40 }}
                  >
                    <XAxis
                      dataKey="month"
                      label={{
                        value: "Month",
                        position: "bottom",
                        offset: 5,
                        style: { fontWeight: "bold" },
                      }}
                    />
                    <YAxis
                      label={{
                        value: "No. of Contracts",
                        angle: -90,
                        position: "left",
                        offset: 5,
                        style: { textAnchor: "middle", fontWeight: "bold" },
                      }}
                    />
                    <Tooltip />
                    <Line dataKey="contracts" stroke="#22c55e" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
